import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../lib/utils';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  className?: string;
}
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full mx-4'
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200" data-id="element-380">
      <div ref={modalRef} className={cn('bg-card text-card-foreground rounded-lg shadow-xl w-full max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200', sizeClasses[size], className)} role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-381">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0" data-id="element-382">
          <div data-id="element-383">
            <h2 id="modal-title" className="text-lg font-semibold text-foreground" data-id="element-384">
              {title}
            </h2>
            {description && <p className="text-sm text-muted-foreground mt-1" data-id="element-385">
                {description}
              </p>}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full hover:bg-muted" aria-label="Close modal" data-id="element-386">
            <X className="h-4 w-4 text-muted-foreground" data-id="element-387" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1" data-id="element-388">{children}</div>

        {/* Footer */}
        {footer && <div className="p-4 border-t border-border bg-muted/50 rounded-b-lg flex justify-end gap-2 shrink-0" data-id="element-389">
            {footer}
          </div>}
      </div>
    </div>;
}